import * as React from 'react'

import { cn } from './cn'

export const TableContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { component?: React.ElementType }
>(({ component: Component = 'div', className, ...props }, ref) => (
  <Component
    ref={ref}
    className={cn('w-full overflow-x-auto', className)}
    {...props}
  />
))
TableContainer.displayName = 'TableContainer'

export const Table = React.forwardRef<
  HTMLTableElement,
  React.TableHTMLAttributes<HTMLTableElement> & {
    size?: 'small' | 'medium'
    stickyHeader?: boolean
  }
>(({ size, stickyHeader, className, ...props }, ref) => (
  <table
    ref={ref}
    data-size={size}
    className={cn('w-full border-collapse text-left text-sm', className)}
    {...props}
  />
))
Table.displayName = 'Table'

export const TableHead = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      '[&_th]:text-xs [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-text-secondary',
      className,
    )}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={className} {...props} />
))
TableBody.displayName = 'TableBody'

export const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot ref={ref} className={className} {...props} />
))
TableFooter.displayName = 'TableFooter'

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & { hover?: boolean }
>(({ hover, className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-divider last:border-b-0',
      hover &&
        'transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
      className,
    )}
    {...props}
  />
))
TableRow.displayName = 'TableRow'

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    component?: 'td' | 'th'
    padding?: 'normal' | 'checkbox' | 'none'
  }
>(({ component, padding = 'normal', align, className, ...props }, ref) => {
  const Component = component ?? 'td'
  return (
    <Component
      ref={ref}
      className={cn(
        padding === 'normal' && 'px-4 py-3',
        padding === 'checkbox' && 'w-12 px-1 py-0',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
      {...props}
    />
  )
})
TableCell.displayName = 'TableCell'
